import InputAdornment from "@material-ui/core/InputAdornment";
import { useHistory, useLocation } from "react-router";
import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect, useState } from "react";
import AddAlert from "@material-ui/icons/AddAlert";
import Email from "@material-ui/icons/Email";
import { useSelector } from "react-redux";
import Icon from "@material-ui/core/Icon";

import { FETCH_LOGIN_REQUEST, RESET_PASSWORD_REQUEST, RESET_PASSWORD_UPDATE_REQUEST, BACK_TO_OTP_REQUEST } from "../../redux/users/actions";
import styles from "assets/jss/material-dashboard-pro-react/views/newLoginPageStyle.js";
import { DEFAULT_API_ENDPOINT, updateApiBaseUrl } from "../../common/constants";
import CustomInput from "components/CustomInput/CustomInput.js";
import { useComposeDispatcher } from "../../common/hooks";
import VersionSelector from "components/VersionSelector";
import Button from "components/CustomButtons/Button.js";
import Snackbar from "components/Snackbar/Snackbar.js";
import loginLogo from "assets/img/login-logo.png";
import Loader from "components/Loader/Loader.js";
import { hasQueryParam } from "common/helpers";
import pjson from "../../../package.json";

const useStyles = makeStyles(styles);

const BRAND = "#E73F3B";

/* Inline SVGs keep the graphics crisp and on-brand without pulling in new image assets */
const IconHeart = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 8.6c0 4.7-8.8 10.1-8.8 10.1S3.2 13.3 3.2 8.6a4.6 4.6 0 0 1 8.8-1.8 4.6 4.6 0 0 1 8.8 1.8z" />
    <path d="M3.6 12h4l1.6-2.6L11.6 15l2-4 1.4 1h5.4" />
  </svg>
);

const IconBreath = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v8" />
    <path d="M12 11c0-2.4-2-4.2-4.2-4.2S3.6 8.6 3.6 11c0 4.4 1.4 8.4 3.6 9.4 1.6.7 2.6-.4 2.8-2 .2-1.4 0-3.4 0-6.4" />
    <path d="M12 11c0-2.4 2-4.2 4.2-4.2S20.4 8.6 20.4 11c0 4.4-1.4 8.4-3.6 9.4-1.6.7-2.6-.4-2.8-2-.2-1.4 0-3.4 0-6.4" />
  </svg>
);

const IconBell = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8.5a6 6 0 0 0-12 0c0 6.5-2.5 8.5-2.5 8.5h17S18 15 18 8.5z" />
    <path d="M10.3 20.5a1.9 1.9 0 0 0 3.4 0" />
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={BRAND} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

const HeroEcg = ({ className, lineClassName }) => (
  <svg className={className} viewBox="0 0 800 200" preserveAspectRatio="none" fill="none">
    <path
      className={lineClassName}
      d="M0 120 H120 l18-58 22 104 20-46 16 22h40l18-70 20 84 18-38 16 12h60l20-52 20 66 18-28 14 10h80l18-44 20 58 18-24 14 10h120"
      stroke={BRAND}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HIGHLIGHTS = [
  {
    title: "Real-time heart rate",
    Icon: IconHeart,
    text: "Beat-to-beat monitoring through a wall-mounted radar sensor — no wearables, no cameras and no patient interaction required.",
  },
  {
    title: "Respiratory rate",
    Icon: IconBreath,
    text: "Continuous breath-rate tracking, so early signs of respiratory deterioration are visible long before they escalate.",
  },
  {
    title: "Events",
    Icon: IconBell,
    text: "Exception reporting pushes real-time events within seconds to designated clinical staff on any connected smart device.",
  },
];

export default function NewLoginPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [selectedVersion, setSelectedVersion] = useState(localStorage.getItem("selected-api-version") || DEFAULT_API_ENDPOINT);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVersionSelector, setShowVersionSelector] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSection, setLoginSection] = useState(true);
  const [otpSection, setOtpSection] = useState(false);
  const [loginInfo, setLoginInfo] = useState({});
  const [trMsg, setTRMsg] = useState("");
  const [tr, setTR] = useState(false);

  const userData = useSelector(({ users }) => users);

  const [resetPasswordUpdate] = useComposeDispatcher(RESET_PASSWORD_UPDATE_REQUEST);
  const [resetPassword] = useComposeDispatcher(RESET_PASSWORD_REQUEST);
  const [backToOTP] = useComposeDispatcher(BACK_TO_OTP_REQUEST);
  const [login] = useComposeDispatcher(FETCH_LOGIN_REQUEST);

  useEffect(() => {
    if (userData.user?.userId && localStorage.getItem("user-details")) {
      history.push("/admin/dashboard");
    }
  });

  useEffect(() => {
    setShowVersionSelector(hasQueryParam("showbanner"));
  }, [location.search]);

  /* Reset-password flow moves forward/back on the redux flags, same as the classic login page */
  if (userData.sentResetPassword && !otpSection) {
    setOtpSection(true);
  }

  if (userData.resetPasswordStatus && otpSection) {
    setOtpSection(false);
    setLoginInfo({});
    setLoginSection(true);
  }

  const handleVersionChange = (newVersion) => {
    setSelectedVersion(newVersion);
    localStorage.setItem("selected-api-version", newVersion);
    updateApiBaseUrl();
    window.location.reload();
  };

  const submitLogin = () => {
    if (loginInfo?.emailAddress && loginInfo?.password) {
      login({ ...loginInfo, emailAddress: loginInfo.emailAddress.trim() });
    } else {
      setTRMsg("All fields are mandatory.");
      setTR(true);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && loginSection && !otpSection) {
      submitLogin();
    }
  };

  const validateResetPasswordUpdate = (data) => {
    const passwordReq = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
    if (!data.emailAddress || !data.confirmPassword || !data.newPassword || !data.otpCode) {
      setTRMsg("All fields are mandatory.");
      setTR(true);
    } else if (!passwordReq.test(data?.newPassword)) {
      setTRMsg("Please enter valid password");
      setTR(true);
    } else if (data?.confirmPassword !== data.newPassword) {
      setTRMsg("Entered Password is not matching");
      setTR(true);
    } else {
      resetPasswordUpdate(data);
    }
  };

  const passwordAdornment = (visible, toggle) => ({
    endAdornment: (
      <InputAdornment position="end">
        <Icon className={classes.inputAdornmentIcon} style={{ cursor: "pointer" }} onClick={toggle}>
          {visible ? "visibility_off" : "visibility"}
        </Icon>
      </InputAdornment>
    ),
    type: visible ? "text" : "password",
    autoComplete: "off",
  });

  const cardCopy = () => {
    if (otpSection) return { title: "Set a new password", subtitle: "Enter the code we emailed you, then choose a new password." };
    if (!loginSection) return { title: "Reset your password", subtitle: "We'll email a one-time code to your registered address." };
    return { title: "Sign in to your portal", subtitle: "Live vitals, events and trends for every device you manage." };
  };

  const { title, subtitle } = cardCopy();

  return (
    <div className={classes.page}>
      <section className={classes.hero}>
        <div className={classes.heroGrid} />
        <HeroEcg className={classes.ecg} lineClassName={classes.ecgLine} />

        <div className={classes.container}>
          <div className={classes.topBar}>
            <img src={loginLogo} alt="Cardi/o" className={classes.topLogo} />
            <a className={classes.topLink} href="https://cardio.io/" target="_blank" rel="noopener noreferrer">
              Learn more at cardio.io →
            </a>
          </div>

          <div className={classes.heroInner}>
            <div className={classes.heroCopy}>
              <span className={classes.eyebrow}>Cardi/o® Care Portal</span>
              <h1 className={classes.heroTitle}>
              A Smoke Detector for the <span className={classes.heroAccent}>human heart</span>
              </h1>
              <p className={classes.heroLead}>
                Cardi/o® continuously monitors heart rate, respiratory rate, sleep patterns, movement and heart rate variability — with no wearables and no
                patient interaction required. Sign in to see live vitals, events and long-term trends across every device you manage.
              </p>

              <div className={classes.highlightRow}>
                {HIGHLIGHTS.map(({ title: highlightTitle, text, Icon: HighlightIcon }) => (
                  <div className={classes.highlight} key={highlightTitle}>
                    <div className={classes.highlightIcon}>
                      <HighlightIcon />
                    </div>
                    <h3 className={classes.highlightTitle}>{highlightTitle}</h3>
                    <p className={classes.highlightText}>{text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={classes.heroForm}>
              <form className={classes.card} onKeyPress={handleKeyPress}>
                <h2 className={classes.cardTitle}>{title}</h2>
                <p className={classes.cardSubtitle}>{subtitle}</p>

                {showVersionSelector && (
                  <div className={classes.versionBox}>
                    <VersionSelector selectedVersion={selectedVersion} onVersionChange={handleVersionChange} />
                  </div>
                )}

                {otpSection && (
                  <>
                    <CustomInput
                      labelText="OTP Code"
                      id="otpCode"
                      formControlProps={{ fullWidth: true }}
                      data={loginInfo}
                      onChange={setLoginInfo}
                    />
                    <CustomInput
                      labelText="New Password"
                      id="newPassword"
                      formControlProps={{ fullWidth: true }}
                      data={loginInfo}
                      onChange={setLoginInfo}
                      inputProps={passwordAdornment(showNewPassword, () => setShowNewPassword(!showNewPassword))}
                    />
                    <CustomInput
                      labelText="Confirm Password"
                      id="confirmPassword"
                      formControlProps={{ fullWidth: true }}
                      helperText="Min 8 characters with a number, a symbol, and upper and lower case letters"
                      data={loginInfo}
                      onChange={setLoginInfo}
                      inputProps={passwordAdornment(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword))}
                    />
                  </>
                )}

                {!otpSection && (
                  <CustomInput
                    labelText="Email"
                    id="emailAddress"
                    formControlProps={{ fullWidth: true }}
                    data={loginInfo}
                    onChange={setLoginInfo}
                    inputProps={{
                      autoFocus: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Email className={classes.inputAdornmentIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                {loginSection && !otpSection && (
                  <CustomInput
                    labelText="Password"
                    id="password"
                    formControlProps={{ fullWidth: true }}
                    data={loginInfo}
                    onChange={setLoginInfo}
                    inputProps={passwordAdornment(showPassword, () => setShowPassword(!showPassword))}
                  />
                )}

                <div className={classes.actions}>
                  {loginSection && !otpSection && (
                    <>
                      <Button color="danger" block onClick={submitLogin}>
                        Login
                      </Button>
                      <button type="button" className={classes.linkButton} onClick={() => setLoginSection(false)}>
                        Forgot your password?
                      </button>
                    </>
                  )}

                  {otpSection && (
                    <>
                      <Button color="danger" block onClick={() => validateResetPasswordUpdate(loginInfo)}>
                        Continue
                      </Button>
                      <button
                        type="button"
                        className={classes.linkButton}
                        onClick={() => {
                          backToOTP();
                          setOtpSection(false);
                        }}
                      >
                        Back to reset
                      </button>
                    </>
                  )}

                  {!loginSection && !otpSection && (
                    <>
                      <Button
                        color="danger"
                        block
                        onClick={() => {
                          if (loginInfo?.emailAddress) {
                            resetPassword(loginInfo);
                          } else {
                            setTRMsg("All fields are mandatory.");
                            setTR(true);
                          }
                        }}
                      >
                        Send reset code
                      </Button>
                      <button type="button" className={classes.linkButton} onClick={() => setLoginSection(true)}>
                        Back to login
                      </button>
                    </>
                  )}
                </div>

                <div className={classes.cardFooter}>
                  <span className={classes.cardFooterMark}>
                    <IconCheck /> Secure, HIPAA-compliant cloud
                  </span>
                  <span>v{pjson.version}</span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Snackbar place="tr" color="danger" icon={AddAlert} message={trMsg} open={tr} closeNotification={() => setTR(false)} close />
      <Loader status={userData.loading} />
    </div>
  );
}
